import { useCallback } from "react"
import { useMutation } from "urql"

import "@/screens/Companies/edit/graphql/create-new-comment"

import type {
  CommentCreateFailure,
  CreateCommentMutationMutation,
  CreateCommentMutationMutationVariables,
} from "@/graphql/codegen/graphql"
import { CommentParentTableType, CreateCommentMutationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface CreateInvoiceCommentInput {
  invoiceId: string
  value: string
}

export interface UseCreateInvoiceCommentReturn {
  createInvoiceComment: (input: CreateInvoiceCommentInput) => Promise<boolean>
  isCreating: boolean
}

// Sole writer to the Comment table for invoice comments. The FE deliberately passes
// `comment: undefined` on CreateInvoiceInput (and equivalents) so the BE handler's
// optional Comment write does not fire — this chained mutation is the single writer, which
// keeps the expanded-row Comments pane (CommentListQuery filtered by
// referenceId + parentTableType = INVOICE) free of duplicate rows.
export const useCreateInvoiceComment = (): UseCreateInvoiceCommentReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    CreateCommentMutationMutation,
    CreateCommentMutationMutationVariables
  >(CreateCommentMutationDocument)

  const createInvoiceComment = useCallback(
    async ({ invoiceId, value }: CreateInvoiceCommentInput): Promise<boolean> => {
      const { data, error } = await executeMutation({
        input: { value, referenceId: invoiceId, type: CommentParentTableType.Invoice },
      })
      const output = data?.comment.create
      const hasError = error || output?.__typename === "CommentCreateFailure"

      if (hasError) {
        const message = error?.message ?? (output as CommentCreateFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      return true
    },
    [executeMutation, notify]
  )

  return { createInvoiceComment, isCreating: fetching }
}
